import React, { useLayoutEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { MISS_AT, TARGET, plural, shortDate } from "./callMeta.js";

// The Growth tab's charts. Hand-rolled SVG like the radar: no chart library,
// and every chart follows the same few rules:
//
//   * one line at a time -- the weekly chart shows Overall, How you sound or
//     What you say, picked above it, never three lines to untangle;
//   * one x position per week, shared by the dot and the call count under it,
//     so a week's score and how many calls made it always line up;
//   * the two bands that matter are painted on, and named in a legend: 7 and
//     up is on target, 5 and below is a miss;
//   * thin weeks (a single call) are drawn HOLLOW, so one lucky call never
//     reads as a trend; weeks with no calls break the line rather than being
//     quietly joined across;
//   * a tooltip on hover AND keyboard focus, and a table view carrying every
//     value, so nothing is gated behind a hover.

const T = { primary: "var(--c-900)", secondary: "var(--c-700)", muted: "var(--c-500)" };

export function fmt(v) {
  return v == null ? "—" : Number(v).toFixed(1);
}

// The chart is drawn at the width it is actually given, so 11px text is 11px
// on screen. A fixed viewBox stretched to 100% scaled every label with the
// card -- axis ticks came out at 15px on a wide screen.
//
// The SVG itself is sized `width: 100%` (see SVG_BOX), never width={W}: a
// hard pixel width made the chart the widest thing in the card, the card grew
// to fit it, the chart measured the grown card, and a phone got a 640px page
// it had to scroll sideways.
function useWidth(fallback = 640) {
  const ref = useRef(null);
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setWidth(Math.max(240, Math.round(el.getBoundingClientRect().width)));
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

const SVG_BOX = { display: "block", overflow: "visible", width: "100%" };

function Tooltip({ leftPct, top = 8, children }) {
  // Kept inside the card: flips to the left of the pointer past the middle.
  const flip = leftPct > 55;
  return (
    <div className="sr-tip" style={{ top, left: flip ? undefined : `calc(${leftPct}% + 14px)`,
                                     right: flip ? `calc(${100 - leftPct}% + 14px)` : undefined }}>
      {children}
    </div>
  );
}

// The two score bands every chart here paints, and the legend that names them.
function Zones({ x, width, yOf, target, missAt }) {
  return (
    <>
      <rect x={x} width={width} y={yOf(10)} height={yOf(target) - yOf(10)} fill="var(--sr-good-wash)" />
      <rect x={x} width={width} y={yOf(missAt)} height={yOf(0) - yOf(missAt)} fill="var(--sr-bad-wash)" />
      <line x1={x} x2={x + width} y1={yOf(target)} y2={yOf(target)} stroke="var(--sr-good-line)" strokeWidth="1.5" />
    </>
  );
}

export function ZoneLegend({ target = TARGET, missAt = MISS_AT }) {
  const swatch = (bg, line) => (
    <span aria-hidden="true" style={{ width: 12, height: 10, borderRadius: 2, background: bg,
                                      borderTop: line ? `2px solid ${line}` : undefined, flexShrink: 0 }} />
  );
  return (
    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11.5, color: T.secondary }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        {swatch("var(--sr-good-wash-key)", "var(--sr-good-line)")}On target ({target} or more)
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        {swatch("var(--sr-bad-wash-key)")}A miss ({missAt} or below)
      </span>
    </div>
  );
}

// ── Week by week: one score per week, and how many calls made it ─────────────

export function WeeklyScoreChart({ weeks = [], seriesKey = "average", seriesLabel = "Average score",
                                   selected = null, onSelect, target = TARGET, missAt = MISS_AT }) {
  const [boxRef, W] = useWidth();
  const [hover, setHover] = useState(null);
  const n = weeks.length;
  const narrow = W < 520;
  const H = 236, padL = 30, padR = narrow ? 34 : 42, padT = 12, padB = 46;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const slot = plotW / Math.max(1, n);
  const xOf = (i) => padL + slot * (i + 0.5);
  const yOf = (v) => padT + (1 - v / 10) * plotH;
  // Label every week when there is room, else every other one (always
  // keeping the latest week, the one a reader looks for first).
  const every = slot >= 48 ? 1 : slot >= 26 ? 2 : 3;
  const labelled = (i) => (n - 1 - i) % every === 0;

  let d = "", open = false;
  weeks.forEach((w, i) => {
    const v = w[seriesKey];
    if (v == null) { open = false; return; }
    d += `${open ? "L" : "M"}${xOf(i).toFixed(1)},${yOf(v).toFixed(1)} `;
    open = true;
  });
  const lastIdx = [...weeks.keys()].reverse().find((k) => weeks[k][seriesKey] != null);
  const shown = hover ?? null;
  const hovered = shown != null ? weeks[shown] : null;

  // Keyboard moves the selection AND shows the tooltip a hover would, so a
  // keyboard reader gets the same readout as a mouse.
  const onKey = (e) => {
    if (!n) return;
    const cur = selected ?? n - 1;
    const next = { ArrowRight: Math.min(n - 1, cur + 1), ArrowLeft: Math.max(0, cur - 1), Home: 0, End: n - 1 }[e.key];
    if (next == null) return;
    e.preventDefault();
    onSelect?.(next);
    setHover(next);
  };

  return (
    <div ref={boxRef} style={{ position: "relative", minWidth: 0 }}>
      <svg viewBox={`0 0 ${W} ${H}`} height={H} role="img" tabIndex={0} className="sr-hit"
           aria-label={`${seriesLabel} each week, out of 10, for the last ${n} weeks. Target is ${target}. `
                       + "Use the left and right arrow keys to pick a week and see its calls below."}
           onKeyDown={onKey} onMouseLeave={() => setHover(null)} onBlur={() => setHover(null)} style={SVG_BOX}>
        <Zones x={padL} width={plotW} yOf={yOf} target={target} missAt={missAt} />
        {[0, 5, 10].map((t) => (
          <line key={t} x1={padL} x2={padL + plotW} y1={yOf(t)} y2={yOf(t)} stroke="var(--sr-grid)" strokeWidth="1" />
        ))}
        {[0, missAt, target, 10].map((t) => (
          <text key={t} x={padL - 8} y={yOf(t) + 3.5} textAnchor="end" fontSize="10.5"
                fill={t === target ? T.secondary : T.muted} fontWeight={t === target ? 650 : 400}
                style={{ fontVariantNumeric: "tabular-nums" }}>{t}</text>
        ))}

        {/* The selected and the hovered week: a column wash behind the dot. */}
        {[selected, hover].map((i, k) => (i == null ? null : (
          <rect key={k} x={xOf(i) - slot / 2 + 1} width={Math.max(2, slot - 2)} y={padT} height={plotH}
                rx="4" fill="var(--sr-select)" opacity={k === 0 ? 1 : 0.6} />
        )))}

        <path d={d.trim()} fill="none" stroke="var(--sr-series-1)" strokeWidth="2"
              strokeLinejoin="round" strokeLinecap="round" />
        {weeks.map((w, i) => (w[seriesKey] == null ? null : (
          <circle key={i} cx={xOf(i)} cy={yOf(w[seriesKey])} r={i === selected || i === hover ? 6 : 4.5}
                  fill={w.thin ? "var(--sr-surface)" : "var(--sr-series-1)"}
                  stroke={w.thin ? "var(--sr-series-1)" : "var(--sr-surface)"} strokeWidth="2" />
        )))}
        {lastIdx != null && (
          <text x={xOf(lastIdx) + 10} y={yOf(weeks[lastIdx][seriesKey]) + 4} fontSize="12" fontWeight="700"
                fill={T.primary}>{fmt(weeks[lastIdx][seriesKey])}</text>
        )}

        {/* Two rows under the plot: the week, and how many calls it held. */}
        <text x={padL - 8} y={H - 9} textAnchor="end" fontSize="10" fill={T.muted}>calls</text>
        {weeks.map((w, i) => (
          <g key={`x${i}`}>
            {labelled(i) && (
              <text x={xOf(i)} y={H - 25} textAnchor="middle" fontSize="10.5"
                    fill={i === selected ? T.primary : T.muted} fontWeight={i === selected ? 700 : 400}>
                {shortDate(w.week_start)}
              </text>
            )}
            <text x={xOf(i)} y={H - 9} textAnchor="middle" fontSize="10.5"
                  fill={w.calls ? T.secondary : T.muted} fontWeight={i === selected ? 700 : 400}
                  style={{ fontVariantNumeric: "tabular-nums" }}>
              {w.calls || "–"}
            </text>
          </g>
        ))}

        {/* One hit column per week: the whole slot, not the 9px dot. */}
        {weeks.map((w, i) => (
          <rect key={`hit${i}`} x={xOf(i) - slot / 2} width={slot} y={0} height={H} fill="transparent"
                style={{ cursor: "pointer" }}
                onMouseEnter={() => setHover(i)} onClick={() => onSelect?.(i)} />
        ))}
      </svg>

      {hovered && (
        <Tooltip leftPct={(xOf(shown) / W) * 100}>
          <div style={{ marginBottom: 3 }}>Week of <strong>{shortDate(hovered.week_start)}</strong></div>
          {hovered.calls ? (
            <>
              <div><strong>{fmt(hovered[seriesKey])}</strong> {seriesLabel.toLowerCase()}</div>
              <div style={{ color: T.muted, marginTop: 2 }}>
                {plural(hovered.calls, "call")} · {hovered.calls_with_miss ?? 0} with a miss
              </div>
              {hovered.thin && <div style={{ color: T.muted }}>Only one call: not a trend</div>}
              {hovered[seriesKey] == null && <div style={{ color: T.muted }}>Nothing scored on this side</div>}
              <div style={{ color: T.muted, marginTop: 3 }}>Click to see the calls</div>
            </>
          ) : <div style={{ color: T.muted }}>No calls scored this week</div>}
        </Tooltip>
      )}
    </div>
  );
}

// ── One skill, every call it was judged on ───────────────────────────────────

export function ScoreStrip({ points = [], target = TARGET, missAt = MISS_AT, onOpenCall, label = "score" }) {
  const [boxRef, W] = useWidth(520);
  const [hover, setHover] = useState(null);
  const H = 110, padL = 26, padR = 14, padT = 8, padB = 20;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const n = points.length;
  const inset = 10;   // keeps the first and last dot clear of the axis labels
  const xOf = (i) => padL + inset + (n <= 1 ? (plotW - 2 * inset) / 2 : (i / (n - 1)) * (plotW - 2 * inset));
  const yOf = (v) => padT + (1 - v / 10) * plotH;
  const d = points.map((p, i) => `${i ? "L" : "M"}${xOf(i).toFixed(1)},${yOf(p.score).toFixed(1)}`).join(" ");
  const hovered = hover != null ? points[hover] : null;
  if (!n) return null;

  return (
    <div ref={boxRef} style={{ position: "relative", minWidth: 0 }}>
      <svg viewBox={`0 0 ${W} ${H}`} height={H} role="img"
           aria-label={`Every ${label} score, oldest first: ${points.map((p) => p.score).join(", ")}.`}
           style={SVG_BOX} onMouseLeave={() => setHover(null)}>
        <Zones x={padL} width={plotW} yOf={yOf} target={target} missAt={missAt} />
        {[0, 5, 10].map((t) => (
          <g key={t}>
            <line x1={padL} x2={padL + plotW} y1={yOf(t)} y2={yOf(t)} stroke="var(--sr-grid)" strokeWidth="1" />
            <text x={padL - 7} y={yOf(t) + 3.5} textAnchor="end" fontSize="10" fill={T.muted}>{t}</text>
          </g>
        ))}
        <path d={d} fill="none" stroke="var(--sr-series-1)" strokeWidth="1.5" opacity="0.45" strokeLinejoin="round" />
        {points.map((p, i) => (
          <circle key={i} cx={xOf(i)} cy={yOf(p.score)} r={hover === i ? 6 : 4.5} fill="var(--sr-series-1)"
                  stroke="var(--sr-surface)" strokeWidth="2" />
        ))}
        <text x={padL} y={H - 4} fontSize="10" fill={T.muted}>{shortDate(points[0].call_date)}</text>
        {n > 1 && <text x={padL + plotW} y={H - 4} textAnchor="end" fontSize="10" fill={T.muted}>{shortDate(points[n - 1].call_date)}</text>}
        {/* 24px hit targets around each 9px dot. */}
        {points.map((p, i) => (
          <circle key={`hit${i}`} cx={xOf(i)} cy={yOf(p.score)} r="12" fill="transparent"
                  tabIndex={p.call_id && onOpenCall ? 0 : -1} className="sr-hit"
                  role={p.call_id && onOpenCall ? "button" : undefined}
                  aria-label={`${shortDate(p.call_date)}, ${p.company_name || p.call_label || "call"}: ${p.score} out of 10`}
                  style={{ cursor: p.call_id && onOpenCall ? "pointer" : "default" }}
                  onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} onBlur={() => setHover(null)}
                  onClick={() => p.call_id && onOpenCall?.(p.call_id)}
                  onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && p.call_id) { e.preventDefault(); onOpenCall?.(p.call_id); } }} />
        ))}
      </svg>
      {hovered && (
        <Tooltip leftPct={(xOf(hover) / W) * 100} top={0}>
          <div><strong>{hovered.score} / 10</strong> · {shortDate(hovered.call_date)}</div>
          <div style={{ color: T.muted }}>{hovered.company_name || hovered.call_label || (hovered.call_id ? "Call" : "From the CRM")}</div>
          {hovered.call_id && onOpenCall && <div style={{ color: T.muted, marginTop: 2 }}>Click to open the call</div>}
        </Tooltip>
      )}
    </div>
  );
}

// ── Before -> now on one 0-10 track ──────────────────────────────────────────

const TONE = { up: "var(--sr-good-mark)", down: "var(--sr-bad-mark)", flat: "var(--c-400, #8a93a3)" };

export function BeforeNow({ before, now, tone = "flat", target = TARGET }) {
  const pct = (v) => (Math.max(0, Math.min(10, v)) / 10) * 100;
  const both = before != null && now != null;
  const lo = both ? Math.min(pct(before), pct(now)) : 0;
  const hi = both ? Math.max(pct(before), pct(now)) : 0;
  const right = both && now >= before;
  return (
    <div aria-hidden="true" style={{ position: "relative", height: 22 }}>
      <div style={{ position: "absolute", left: 0, right: 0, top: 10, height: 2, borderRadius: 2, background: "var(--sr-grid)" }} />
      <div style={{ position: "absolute", left: `${pct(target)}%`, top: 3, width: 2, height: 16, borderRadius: 1,
                    background: "var(--sr-good-line)" }} />
      {both && hi - lo > 1.5 && (
        <div style={{ position: "absolute", top: 9.5, height: 3, borderRadius: 2, left: `${lo}%`, width: `${hi - lo}%`,
                      background: TONE[tone] }} />
      )}
      {/* Arrow head on the "now" end, so direction never rests on colour --
          only where there is room for it between the two dots. */}
      {both && hi - lo >= 7 && (
        <>
          <div style={{ position: "absolute", top: 6, left: `calc(${pct(now)}% + ${right ? -14 : 8}px)`,
                        width: 0, height: 0, borderTop: "5px solid transparent", borderBottom: "5px solid transparent",
                        [right ? "borderLeft" : "borderRight"]: `6px solid ${TONE[tone]}` }} />
        </>
      )}
      {before != null && (
        <span style={{ position: "absolute", top: 5, left: `calc(${pct(before)}% - 6px)`, width: 8, height: 8,
                       borderRadius: 999, background: "var(--sr-surface)", border: "2px solid var(--c-500)" }} />
      )}
      {now != null && (
        <span style={{ position: "absolute", top: 4, left: `calc(${pct(now)}% - 7px)`, width: 10, height: 10,
                       borderRadius: 999, background: "var(--sr-series-1)", border: "2px solid var(--sr-surface)" }} />
      )}
    </div>
  );
}

// ── A run of calls on one skill: a square each, a cross on a miss ────────────

export function RunSquares({ recent = [], missAt = MISS_AT }) {
  return (
    <span style={{ display: "inline-flex", gap: 3, alignItems: "center" }}
          aria-label={`Last ${recent.length} calls on this skill, oldest first: `
                      + recent.map((r) => (r.miss ? `miss (${r.score})` : `${r.score}`)).join(", ")}>
      {recent.map((r, i) => (
        <span key={i} title={`${r.crm ? "Weekly CRM check, " : ""}${shortDate(r.call_date)} · ${r.score}/10`
                             + `${r.miss ? ` · a miss (${missAt} or below)` : ""}`}
              style={{ width: 14, height: 14, borderRadius: 3, display: "inline-flex", alignItems: "center",
                       justifyContent: "center", boxSizing: "border-box",
                       background: r.miss ? "transparent" : "var(--sr-good-mark)",
                       border: r.miss ? "1.5px solid var(--sr-bad-mark)" : "none" }}>
          {r.miss && <X size={10} strokeWidth={3} color="var(--sr-bad-mark)" />}
        </span>
      ))}
    </span>
  );
}

// ── The table twin the weekly chart gets ─────────────────────────────────────

export function WeeksTable({ weeks = [] }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="sr-table">
        <thead>
          <tr><th>Week of</th><th className="num">Calls</th><th className="num">With a miss</th>
            <th className="num">Overall</th><th className="num">How you sound</th><th className="num">What you say</th></tr>
        </thead>
        <tbody>
          {weeks.map((w) => (
            <tr key={w.week_start}>
              <td>{shortDate(w.week_start)}{w.thin ? " (1 call)" : ""}</td>
              <td className="num">{w.calls}</td>
              <td className="num">{w.calls ? w.calls_with_miss : "—"}</td>
              <td className="num">{fmt(w.average)}</td>
              <td className="num">{fmt(w.tonality)}</td>
              <td className="num">{fmt(w.technicality)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
